import type { AuthUser, LoginPayload, LoginResponse } from '@/types/auth'
import { ApiError, isRecord, request } from './http'

const isStringList = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string')

/**
 * The account in an answer of the API, or null when it is not one. Permissions that are missing
 * count as none: the screens then show only what needs none.
 */
function readAuthUser(value: unknown): AuthUser | null {
  if (
    !isRecord(value) ||
    typeof value.id !== 'string' ||
    typeof value.email !== 'string' ||
    !(value.name === null || typeof value.name === 'string') ||
    !isStringList(value.roles)
  ) {
    return null
  }
  if (value.permissions !== undefined && !isStringList(value.permissions)) return null
  return {
    id: value.id,
    email: value.email,
    name: value.name,
    roles: value.roles,
    permissions: value.permissions ?? [],
  }
}

function readLoginResponse(value: unknown): LoginResponse | null {
  if (
    !isRecord(value) ||
    typeof value.token !== 'string' ||
    value.token === '' ||
    typeof value.expiresAt !== 'string'
  ) {
    return null
  }
  const user = readAuthUser(value.user)
  return user && { token: value.token, tokenType: 'Bearer', expiresAt: value.expiresAt, user }
}

/** A 2xx answer that is not what the API documents, e.g. a proxy's error page. */
const unexpected = () =>
  new ApiError(0, 'unexpected_response', 'The API answered with something unexpected.')

/** Logs in with an e-mail address and a password. */
export async function login(payload: LoginPayload): Promise<LoginResponse> {
  const session = readLoginResponse(await request('/v1/auth/login', { method: 'POST', body: payload }))
  if (!session) throw unexpected()
  return session
}

/** The account a token was issued to. Fails with status 401 when the token is no longer valid. */
export async function fetchCurrentUser(token: string): Promise<AuthUser> {
  const user = readAuthUser(await request('/v1/auth/me', { token }))
  if (!user) throw unexpected()
  return user
}
